import React, { useState, useEffect, useMemo } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import {
  MultiSelectHierarchicalFilterDropdown,
  FilterDropdown,
} from "components/leads/filters";
import { DATE_RANGES } from "utils/reportFilters";
import { DatePicker } from "components/shared/form/Datepicker";

export function ReportsFilterDrawer({
  isOpen,
  onClose,
  filters,
  onApply,
  onReset,
  realAgents = [],
  agents = [],
  realSources = [],
  stageOptions = [],
  pageMeta = {},
}) {
  const [localFilters, setLocalFilters] = useState(filters);

  // Sync when drawer opens or filters update
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
    }
  }, [isOpen, filters]);

  // Options memoization for Date Range
  const dateRangeOptions = useMemo(
    () => [
      { id: DATE_RANGES.ALL, label: "All Time" },
      { id: DATE_RANGES.THIS_MONTH, label: "This Month" },
      { id: DATE_RANGES.LAST_MONTH, label: "Last Month" },
      { id: DATE_RANGES.LAST_3_MONTHS, label: "Last 3 Months" },
      { id: DATE_RANGES.THIS_YEAR, label: "This Year" },
      { id: DATE_RANGES.CUSTOM, label: "Custom" },
    ],
    [],
  );

  // Options memoization for Agents / Users
  const agentOptions = useMemo(() => {
    if (realAgents.length > 0) {
      return realAgents.map((a) => ({
        id: String(a.userId),
        label: a.fullName,
        designation: Array.isArray(a.designations)
          ? a.designations.map(d => d.DesignationName || d.designationName).join(', ')
          : a.designations || a.designationName || a.DesignationName || a.roleName || a.RoleName || ""
      }));
    } else {
      return agents.map((a) => ({ id: a, label: a, designation: "" }));
    }
  }, [realAgents, agents]);

  // Options memoization for Sources
  const sourceOptions = useMemo(() => {
    return realSources.map((s) => {
      const id =
        s.leadSourceId ||
        s.LeadSourceId ||
        s.id ||
        s.Id ||
        s.sourceId ||
        s.SourceId;
      const name =
        s.sourceChannel || s.SourceChannel || s.sourceName || s.SourceName;
      return { id: String(id), label: name };
    });
  }, [realSources]);

  // Options memoization for Comparison Rows & Columns
  const comparisonOptions = useMemo(
    () => [
      { id: "agent", label: "Salesperson" },
      { id: "status", label: "Stage" },
      { id: "source", label: "Lead Source" },
      { id: "inquiry", label: "Inquiry Type" },
      { id: "month", label: "Month" },
    ],
    [],
  );

  const selectedStageValues = useMemo(() => {
    const stages = Array.isArray(localFilters.status)
      ? localFilters.status
      : localFilters.status
        ? [localFilters.status]
        : [];
    const subStages = Array.isArray(localFilters.subStatus)
      ? localFilters.subStatus
      : localFilters.subStatus
        ? [localFilters.subStatus]
        : [];
    return [...stages, ...subStages];
  }, [localFilters.status, localFilters.subStatus]);

  const triggerApply = (newFilters) => {
    onApply(newFilters, false);
  };

  const handleStageSelect = (filterKey, selectedIds, type) => {
    if (type === "all" || !selectedIds || selectedIds.length === 0) {
      const updated = { ...localFilters, status: [], subStatus: [] };
      setLocalFilters(updated);
      triggerApply(updated);
      return;
    }

    const stageSet = new Set(stageOptions.map((s) => String(s.id || s.value)));
    const subStageSet = new Set(
      stageOptions.flatMap((s) =>
        (s.children || []).map((sub) => String(sub.id || sub.value)),
      ),
    );

    const parentStageMap = new Map();
    stageOptions.forEach((s) => {
      const stageId = String(s.id || s.value);
      (s.children || []).forEach((sub) => {
        const subStageId = String(sub.id || sub.value);
        parentStageMap.set(subStageId, stageId);
      });
    });

    const selectedStagesSet = new Set(
      selectedIds
        .filter((id) => stageSet.has(String(id)))
        .map((id) => String(id)),
    );

    const selectedSubStages = selectedIds
      .filter((id) => subStageSet.has(String(id)))
      .map((id) => String(id));

    // If a subStage ID is selected, ensure its parent stage ID is also included in stageIds
    selectedSubStages.forEach((subId) => {
      const parentId = parentStageMap.get(String(subId));
      if (parentId) {
        selectedStagesSet.add(parentId);
      }
    });

    const updated = {
      ...localFilters,
      status: Array.from(selectedStagesSet),
      subStatus: selectedSubStages,
    };
    setLocalFilters(updated);
    triggerApply(updated);
  };

  const handleStageClear = () => {
    const updated = { ...localFilters, status: [], subStatus: [] };
    setLocalFilters(updated);
    triggerApply(updated);
  };

  const handleReset = () => {
    onReset();
  };

  if (!isOpen) return null;

  return (
    <div className="dark:bg-dark-800 dark:border-dark-700 relative z-30 w-full rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-end gap-4 lg:flex-nowrap">
        {/* Date Range */}
        <div className="w-full min-w-[200px] sm:w-auto sm:flex-1 lg:max-w-xs">
          <FilterDropdown
            filterKey="quickRange"
            label="Date Range"
            options={dateRangeOptions}
            selectedValue={localFilters.quickRange}
            onSelect={(key, val) => {
              const updated = { ...localFilters, quickRange: val };
              setLocalFilters(updated);
              triggerApply(updated);
            }}
            onClear={() => {
              const updated = { ...localFilters, quickRange: DATE_RANGES.ALL };
              setLocalFilters(updated);
              triggerApply(updated);
            }}
            showAllOption={false}
            showSearch={false}
            placeholder="Select Date Range"
            className="z-40 w-full"
          />
        </div>

        {/* Custom Dates */}
        {localFilters.quickRange === DATE_RANGES.CUSTOM && (
          <div className="flex gap-4">
            <div className="flex w-[140px] flex-col gap-1.5">
              <label className="dark:text-slate-450 mb-0.5 block text-xs font-semibold text-slate-400">
                From Date
              </label>
              <DatePicker
                value={localFilters.fromDate}
                onChange={(dates, dateStr) => {
                  const updated = { ...localFilters, fromDate: dateStr };
                  setLocalFilters(updated);
                  triggerApply(updated);
                }}
                options={{
                  altInput: true,
                  altFormat: "d/m/y",
                  dateFormat: "Y-m-d",
                }}
                className="dark:border-dark-600 dark:bg-dark-900 h-[38px] w-full rounded-lg border border-gray-300 bg-white px-3 text-xs font-semibold"
              />
            </div>
            <div className="flex w-[140px] flex-col gap-1.5">
              <label className="dark:text-slate-450 mb-0.5 block text-xs font-semibold text-slate-400">
                To Date
              </label>
              <DatePicker
                value={localFilters.toDate}
                onChange={(dates, dateStr) => {
                  const updated = { ...localFilters, toDate: dateStr };
                  setLocalFilters(updated);
                  triggerApply(updated);
                }}
                options={{
                  altInput: true,
                  altFormat: "d/m/y",
                  dateFormat: "Y-m-d",
                }}
                className="dark:border-dark-600 dark:bg-dark-900 h-[38px] w-full rounded-lg border border-gray-300 bg-white px-3 text-xs font-semibold"
              />
            </div>
          </div>
        )}

        {/* Users / Agent */}
        {pageMeta.showAgent && (
          <div className="w-full min-w-[200px] sm:w-auto sm:flex-1 lg:max-w-xs">
            <FilterDropdown
              filterKey="agent"
              label="Users"
              options={agentOptions}
              selectedValue={localFilters.agent}
              onSelect={(key, val) => {
                const updated = { ...localFilters, agent: val === "all" || !val ? null : val };
                setLocalFilters(updated);
                triggerApply(updated);
              }}
              onClear={() => {
                const updated = { ...localFilters, agent: null };
                setLocalFilters(updated);
                triggerApply(updated);
              }}
              showAllOption={true}
              allOptionLabel="Select User"
              showSearch={true}
              placeholder="Select User"
              className="z-30 w-full"
            />
          </div>
        )}

        {/* Source */}
        {pageMeta.showSource && (
          <div className="w-full min-w-[200px] sm:w-auto sm:flex-1 lg:max-w-xs">
            <FilterDropdown
              filterKey="leadSource"
              label="Source"
              options={sourceOptions}
              selectedValue={localFilters.leadSource}
              onSelect={(key, val) => {
                const updated = { ...localFilters, leadSource: val };
                setLocalFilters(updated);
                triggerApply(updated);
              }}
              onClear={() => {
                const updated = { ...localFilters, leadSource: "" };
                setLocalFilters(updated);
                triggerApply(updated);
              }}
              showAllOption={true}
              showSearch={true}
              placeholder="All Sources"
              className="z-20 w-full"
            />
          </div>
        )}

        {/* Stage / Sub Stage */}
        {pageMeta.showStatus && (
          <div className="w-full min-w-[200px] sm:w-auto sm:flex-1 lg:max-w-xs">
            <MultiSelectHierarchicalFilterDropdown
              filterKey="stage"
              label="Stage"
              options={stageOptions}
              selectedValues={selectedStageValues}
              onSelect={handleStageSelect}
              onClear={handleStageClear}
              disabled={false}
              placeholder="Select Stage / Sub Stage"
              className="z-10 w-full"
            />
          </div>
        )}

        {/* Comparison Rows & Columns */}
        {pageMeta.isComparison && (
          <>
            <div className="w-full min-w-[200px] sm:w-auto sm:flex-1 lg:max-w-xs">
              <FilterDropdown
                filterKey="rowVar"
                label="Rows"
                options={comparisonOptions}
                selectedValue={localFilters.rowVar}
                onSelect={(key, val) => {
                  const updated = { ...localFilters, rowVar: val };
                  setLocalFilters(updated);
                  triggerApply(updated);
                }}
                onClear={() => {
                  const updated = { ...localFilters, rowVar: "agent" };
                  setLocalFilters(updated);
                  triggerApply(updated);
                }}
                showAllOption={false}
                showSearch={false}
                placeholder="Select Rows"
                className="z-10 w-full"
              />
            </div>
            <div className="w-full min-w-[200px] sm:w-auto sm:flex-1 lg:max-w-xs">
              <FilterDropdown
                filterKey="colVar"
                label="Columns"
                options={comparisonOptions}
                selectedValue={localFilters.colVar}
                onSelect={(key, val) => {
                  const updated = { ...localFilters, colVar: val };
                  setLocalFilters(updated);
                  triggerApply(updated);
                }}
                onClear={() => {
                  const updated = { ...localFilters, colVar: "status" };
                  setLocalFilters(updated);
                  triggerApply(updated);
                }}
                showAllOption={false}
                showSearch={false}
                placeholder="Select Columns"
                className="z-10 w-full"
              />
            </div>
          </>
        )}

        <div className="w-full flex-shrink-0 pb-1 sm:w-auto">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1.5 text-xs font-semibold text-red-500 transition-colors hover:text-red-600"
          >
            <TrashIcon className="h-4 w-4" /> Clear All
          </button>
        </div>
      </div>
    </div>
  );
}
